<script lang="ts">
  import { appVersion, type NativeVersion } from '../native'
  import { availableBasemaps, isBasemapChoice, type BasemapChoice } from '../map/basemaps'
  import { layout } from '../state/layout.svelte'
  import { prefs, setBasemap } from '../state/prefs.svelte'
  import { pushStateLine } from '../state/push.svelte'
  import { setTheme, theme, type ThemeChoice } from '../state/theme.svelte'
  import InstallButton from './pwa/InstallButton.svelte'
  import DefaultLocation from './settings/DefaultLocation.svelte'
  import Notifications from './settings/Notifications.svelte'
  import Places from './settings/Places.svelte'

  /**
   * Every setting, on a screen of its own (round 2's 3.5, Stage 10.5). It is
   * the Settings section on both layouts (Phase 11 plan §2.6): under the
   * section's own bar and scrolled by it, and the nav or the rail is the way
   * out. It was the last
   * collapsible panel before, and a panel at the bottom of a scrolling sheet
   * was the worst place for a list you rename, reorder and paste into.
   *
   * Sections are `<section>`s with headings, so the whole screen reads at
   * once, and a section with nothing in it is not rendered: Notifications
   * appears only once the server says it has push (Phase 9 plan §9.1).
   */

  let { cartoKey }: { cartoKey: string } = $props()

  /** Inside the iOS app, its own version beside the web build's. */
  let app: NativeVersion | null = $state(null)
  void appVersion().then((v) => (app = v))

  let options = $derived(availableBasemaps(cartoKey))

  const THEMES: { id: ThemeChoice; label: string }[] = [
    { id: 'system', label: 'System' },
    { id: 'light', label: 'Light' },
    { id: 'dark', label: 'Dark' },
  ]

  /** What each basemap is for, in the handoff's few words. */
  const NOTES: Partial<Record<BasemapChoice, { name: string; note: string }>> = {
    auto: { name: 'Match theme', note: 'Positron on light, Dark on dark' },
    positron: { name: 'Positron', note: 'quiet and light' },
    dark: { name: 'Dark', note: 'quiet and dark' },
    liberty: { name: 'Liberty', note: 'more roads and labels' },
    'carto-voyager': { name: 'CARTO Voyager', note: 'raster tiles' },
  }

  let basemaps = $derived([
    { id: 'auto' as BasemapChoice, label: 'Match theme' },
    ...options.map((b) => ({ id: b.id as BasemapChoice, label: b.label })),
  ])

  function pickBasemap(id: string) {
    if (isBasemapChoice(id)) setBasemap(id)
  }
</script>

<!-- The section around it is the region, named by its bar. -->
<div class="screen" class:phone={!layout.isDesktop}>
  <div class="scroll">
    <section aria-labelledby="settings-open-to">
      <h3 id="settings-open-to" class="wx-rule eyebrow">Default location</h3>
      <DefaultLocation />
    </section>

    <section aria-labelledby="settings-places">
      <Places headingId="settings-places" />
    </section>

    {#if pushStateLine() !== null}
      <section aria-labelledby="settings-notifications">
        <h3 id="settings-notifications" class="wx-rule eyebrow">Notifications</h3>
        <Notifications />
      </section>
    {/if}

    <section aria-labelledby="settings-map" class="map">
      <h3 id="settings-map" class="wx-rule eyebrow">Map</h3>
      <div class="row" role="group" aria-labelledby="settings-theme">
        <span class="label" id="settings-theme">Theme</span>
        <div class="wx-seg theme">
          {#each THEMES as option (option.id)}
            <label class="seg-opt touch-target" class:on={theme.choice === option.id}>
              <input
                type="radio"
                name="theme"
                value={option.id}
                checked={theme.choice === option.id}
                onchange={() => setTheme(option.id)}
              />
              {option.label}
            </label>
          {/each}
        </div>
      </div>
      <div class="row" role="group" aria-labelledby="settings-basemap">
        <span class="label" id="settings-basemap">Basemap</span>
        <div class="options">
          {#each basemaps as basemap (basemap.id)}
            {@const note = NOTES[basemap.id]}
            <label class="option touch-target">
              <input
                class="wx-box"
                type="radio"
                name="basemap"
                value={basemap.id}
                checked={prefs.basemap === basemap.id}
                onchange={() => pickBasemap(basemap.id)}
              />
              <span
                ><b>{note?.name ?? basemap.label}</b>{#if note}{' '}<span class="muted"
                    >— {note.note}</span
                  >{/if}</span
              >
            </label>
          {/each}
        </div>
      </div>
    </section>

    <section aria-labelledby="settings-about">
      <h3 id="settings-about" class="wx-rule eyebrow">About</h3>
      <div class="about">
        <b>Fusion WX</b>
        <span class="version">{import.meta.env.VITE_APP_VERSION}</span>
      </div>
      {#if app}
        <div class="about">
          <span>iOS app</span>
          <span class="version">{app.version} ({app.build})</span>
        </div>
      {/if}
      <div class="install-slot"><InstallButton /></div>
      <p class="credit">
        Forecasts, alerts and observations from the
        <a href="https://www.weather.gov/documentation/services-web-api" rel="noreferrer">NWS API</a>;
        radar, satellite and lightning from NOAA nowCOAST; outlooks from the Storm Prediction Center; model
        output from NOAA on AWS Open Data. Basemaps from
        <a href="https://openfreemap.org/" rel="noreferrer">OpenFreeMap</a>
        and <a href="https://carto.com/attributions" rel="noreferrer">CARTO</a>, map data ©
        <a href="https://www.openstreetmap.org/copyright" rel="noreferrer">OpenStreetMap</a>
        contributors.
      </p>
    </section>
  </div>
</div>

<style>
  /* The section's panel scrolls it and owns the insets. */
  .screen {
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    color: var(--color-text);
    /* The desktop panel's 24 px line; the phone's 16. */
    --pad: 24px;
  }

  .screen.phone {
    --pad: 16px;
  }

  .scroll {
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 18px var(--pad) 0;
  }

  section {
    display: flex;
    flex-direction: column;
  }

  h3 {
    margin: 0;
    color: var(--color-text);
  }

  .map {
    gap: 10px;
  }

  .row {
    display: grid;
    grid-template-columns: 76px minmax(0, 1fr);
    align-items: start;
    gap: 8px;
  }

  .label {
    font-size: 12px;
    color: var(--color-muted);
    padding-top: 7px;
  }

  .phone .row {
    grid-template-columns: minmax(0, 1fr);
  }

  .phone .label {
    padding-top: 0;
    margin-bottom: 6px;
  }

  .seg-opt {
    display: flex;
    align-items: center;
    height: var(--seg-h, 28px);
    padding: 0 10px;
    background: var(--color-bg);
    font-size: var(--ctl-fs, 12px);
    font-weight: 600;
    cursor: pointer;
  }

  .seg-opt.on {
    background: var(--color-text);
    color: var(--color-bg);
    font-weight: 800;
  }

  .seg-opt input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  .seg-opt:has(input:focus-visible) {
    outline: 2px solid var(--color-accent);
    outline-offset: -4px;
  }

  .options {
    display: flex;
    flex-direction: column;
  }

  .option {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    min-height: 34px;
    border-bottom: 1px solid var(--color-divider);
    font-size: 13px;
    cursor: pointer;
  }

  .option :global(.wx-box) {
    width: 18px;
    height: 18px;
  }

  .option b {
    font-weight: 800;
  }

  .about {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 0;
    border-bottom: 1px solid var(--color-divider);
    font-size: 13px;
  }

  .about b {
    font-weight: 800;
  }

  .version {
    font: 500 12px var(--font-mono);
  }

  .install-slot:not(:empty) {
    padding-top: 12px;
  }

  .credit {
    margin: 10px 0 0;
    font-size: 11px;
    line-height: 1.5;
    color: var(--color-muted);
  }

  .credit a {
    color: var(--color-text);
  }
</style>
